import type { GameSource } from '../../common/gameSource';
import axios from 'axios';
import getApiInstance from '../../common/apiInstance';
import { getApiErrorMessage } from '../../common/apiErrorMessage';
import type { ReplayGameSummary } from '../../replay/replayApi';
import {
  fetchOpeningFamilyBySlug,
  fetchOpeningLineBySlugs,
} from '../../explorer/openingsApi';
import { getOpeningFamilyName } from '../shared/openingLabel';
import {
  openingFamilySlug,
  openingVariationSlug,
} from '../shared/openingBrowseSlugs';
import { variationRowKey } from './openingVariationBranch';
import { GAMES_BROWSE_PAGE_SIZE } from '../shared/gamesPageConstants';

const gamesApi = getApiInstance('/games', 'no-settings');

const openingVariationsCache = new Map<string, OpeningVariation[]>();
const inFlightOpeningVariations = new Map<
  string,
  Promise<OpeningVariation[]>
>();

const openingGamesCache = new Map<
  string,
  PaginatedGamesResult & {
    opening: string;
    eco?: string;
    source: GameSource;
  }
>();
const inFlightOpeningGames = new Map<
  string,
  Promise<
    PaginatedGamesResult & {
      opening: string;
      eco?: string;
      source: GameSource;
    }
  >
>();

const openingVariationsCacheKey = (params: {
  opening: string;
  source: GameSource;
  familyId?: number;
}): string =>
  `${params.source}:${params.familyId ?? 'name'}:${params.opening}`;

const openingGamesCacheKey = (params: {
  opening: string;
  source: GameSource;
  eco?: string;
  familyId?: number;
  lineId?: number;
  limit?: number;
  offset?: number;
  player?: string;
}): string =>
  JSON.stringify({
    opening: params.opening,
    source: params.source,
    eco: params.eco ?? '',
    familyId: params.familyId ?? '',
    lineId: params.lineId ?? '',
    limit: params.limit ?? GAMES_BROWSE_PAGE_SIZE,
    offset: params.offset ?? 0,
    player: params.player?.trim().toLowerCase() ?? '',
  });

export type PlayerSearchResult = {
  username: string;
  title?: string;
  source: GameSource;
  gameCount: number;
  avgRating: number;
  maxRating?: number;
};

export type OpeningSearchResult = {
  opening: string;
  eco?: string;
  source: GameSource;
  gameCount: number;
  maxRating?: number;
  familyId?: number;
};

export type PlayerOpeningStat = {
  opening: string;
  eco?: string;
  count: number;
  percent: number;
  /** Full variation name from the lichess openings reference table. */
  lichessOpening?: string;
  /** Lichess opening movetext for this variation line. */
  pgn?: string;
};

export type OpeningVariation = {
  opening: string;
  eco?: string;
  gameCount: number;
  maxRating?: number;
  lineId?: number;
  /** Full variation name from the lichess openings reference table. */
  lichessOpening?: string;
  /** Lichess opening movetext for this variation line. */
  pgn?: string;
};

export type PaginatedGamesResult = {
  games: ReplayGameSummary[];
  offset: number;
  hasMore: boolean;
};

const gameAvgElo = (game: ReplayGameSummary): number =>
  ((game.whiteElo ?? 0) + (game.blackElo ?? 0)) / 2;

export const mergeOpeningSearchResults = (
  items: OpeningSearchResult[],
): OpeningSearchResult[] => {
  const merged = new Map<string, OpeningSearchResult>();

  for (const item of items) {
    const family = getOpeningFamilyName(item.opening);
    const key =
      item.familyId != null
        ? `id:${item.familyId}`
        : family.toLowerCase();
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, {
        ...item,
        opening: family,
      });
      continue;
    }

    existing.gameCount += item.gameCount;
    existing.maxRating = Math.max(
      existing.maxRating ?? 0,
      item.maxRating ?? 0,
    );
  }

  return [...merged.values()].sort(
    (a, b) =>
      b.gameCount - a.gameCount || a.opening.localeCompare(b.opening),
  );
};

export const mergeOpeningVariations = (
  variationLists: OpeningVariation[][],
): OpeningVariation[] => {
  const merged = new Map<string, OpeningVariation>();

  for (const variations of variationLists) {
    for (const variation of variations) {
      const key =
        variation.lineId != null
          ? `line:${variation.lineId}`
          : variationRowKey(variation);
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, { ...variation });
        continue;
      }

      existing.gameCount += variation.gameCount;
      existing.maxRating = Math.max(
        existing.maxRating ?? 0,
        variation.maxRating ?? 0,
      );
      if (!existing.pgn && variation.pgn) {
        existing.pgn = variation.pgn;
      }
      if (!existing.lichessOpening && variation.lichessOpening) {
        existing.lichessOpening = variation.lichessOpening;
      }
    }
  }

  return [...merged.values()].sort(
    (a, b) =>
      b.gameCount - a.gameCount ||
      a.opening.localeCompare(b.opening) ||
      (a.eco ?? '').localeCompare(b.eco ?? ''),
  );
};

export const fetchOpeningVariationsForSources = async (params: {
  opening: string;
  sources: GameSource[];
  familyId?: number;
}): Promise<OpeningVariation[]> => {
  const family = getOpeningFamilyName(params.opening);
  const results = await Promise.all(
    params.sources.map((source) =>
      fetchOpeningVariations({
        opening: family,
        source,
        familyId: params.familyId,
      }).catch(() => []),
    ),
  );
  return mergeOpeningVariations(results);
};

export const prefetchOpeningVariationsForSources = (params: {
  opening: string;
  sources: GameSource[];
  familyId?: number;
}): void => {
  void fetchOpeningVariationsForSources(params).catch(() => {});
};

export const fetchOpeningGamesForSources = async (params: {
  opening: string;
  sources: GameSource[];
  eco?: string;
  familyId?: number;
  lineId?: number;
  limit?: number;
  offset?: number;
  player?: string;
}): Promise<PaginatedGamesResult> => {
  const opening = params.eco?.trim()
    ? params.opening.trim()
    : getOpeningFamilyName(params.opening);
  const limit = params.limit ?? GAMES_BROWSE_PAGE_SIZE;
  const offset = params.offset ?? 0;
  const fetchLimit = offset + limit;

  const results = await Promise.all(
    params.sources.map((source) =>
      fetchOpeningGames({
        opening,
        source,
        eco: params.eco,
        familyId: params.familyId,
        lineId: params.lineId,
        limit: fetchLimit,
        offset: 0,
        player: params.player,
      }).catch(() => ({
        games: [],
        offset: 0,
        hasMore: false,
        opening,
        source,
      })),
    ),
  );

  const mergedGames = results
    .flatMap((result) => result.games)
    .sort((a, b) => gameAvgElo(b) - gameAvgElo(a));
  const games = mergedGames.slice(offset, offset + limit);
  const hasMore =
    results.some((result) => result.hasMore) ||
    mergedGames.length > offset + limit;

  return { games, offset, hasMore };
};

export const prefetchOpeningGamesForSources = (params: {
  opening: string;
  sources: GameSource[];
  eco?: string;
  familyId?: number;
  lineId?: number;
  limit?: number;
  offset?: number;
  player?: string;
}): void => {
  void fetchOpeningGamesForSources(params).catch(() => {});
};

export type GamesCategoryPreviewParams =
  | {
      kind: 'opening';
      opening: string;
      sources: GameSource[];
      eco?: string;
    }
  | {
      kind: 'player';
      username: string;
      source: GameSource;
      opening?: string;
      eco?: string;
    };

export const gamesCategoryPreviewKey = (
  params: GamesCategoryPreviewParams,
): string => JSON.stringify(params);

export const resolveGamesCategoryPreviewGame = async (
  params: GamesCategoryPreviewParams,
): Promise<{ gameId: string; openingSans?: string[] } | null> => {
  if (params.kind === 'opening') {
    const result = await fetchOpeningGamesForSources({
      opening: params.opening,
      sources: params.sources,
      eco: params.eco,
      limit: 1,
      offset: 0,
    });
    const game = result.games[0];
    return game
      ? { gameId: game.gameId, openingSans: game.openingSans }
      : null;
  }

  const result = await fetchPlayerGames(params.username, params.source, {
    opening: params.opening,
    eco: params.eco,
    limit: 1,
    offset: 0,
  });
  const game = result.games[0];
  return game ? { gameId: game.gameId, openingSans: game.openingSans } : null;
};

export const prefetchGamesCategoryPreview = (
  params: GamesCategoryPreviewParams,
): void => {
  void resolveGamesCategoryPreviewGame(params).catch(() => {});
};

export const searchPlayers = async (params: {
  q: string;
  source: GameSource;
  gmsOnly?: boolean;
}): Promise<PlayerSearchResult[]> => {
  try {
    const { data } = await gamesApi.get<{ players: PlayerSearchResult[] }>(
      '/players/search',
      {
        params: {
          q: params.q,
          source: params.source,
          ...(params.gmsOnly !== undefined
            ? { gmsOnly: params.gmsOnly ? '1' : '0' }
            : {}),
        },
      },
    );
    return data.players;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to search players' }),
    );
  }
};

export const searchOpenings = async (params: {
  q: string;
  source: GameSource;
}): Promise<OpeningSearchResult[]> => {
  try {
    const { data } = await gamesApi.get<{ openings: OpeningSearchResult[] }>(
      '/openings/search',
      {
        params: {
          q: params.q,
          source: params.source,
        },
      },
    );
    return data.openings;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to search openings' }),
    );
  }
};

export const fetchPlayerGames = async (
  username: string,
  source: GameSource,
  params?: {
    limit?: number;
    offset?: number;
    opening?: string;
    eco?: string;
  },
): Promise<PaginatedGamesResult> => {
  try {
    const { data } = await gamesApi.get<{
      games: ReplayGameSummary[];
      offset: number;
      hasMore?: boolean;
    }>(`/players/${encodeURIComponent(username)}/games`, {
      params: {
        source,
        limit: params?.limit ?? GAMES_BROWSE_PAGE_SIZE,
        offset: params?.offset ?? 0,
        ...(params?.opening ? { opening: params.opening } : {}),
        ...(params?.eco ? { eco: params.eco } : {}),
      },
    });
    return {
      games: data.games,
      offset: data.offset ?? params?.offset ?? 0,
      hasMore: data.hasMore === true,
    };
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, {
        action: `Failed to load games for ${username}`,
      }),
    );
  }
};

export const prefetchOpeningVariations = (params: {
  opening: string;
  source: GameSource;
}): void => {
  void fetchOpeningVariations(params).catch(() => {});
};

export const fetchOpeningVariations = async (params: {
  opening: string;
  source: GameSource;
  familyId?: number;
}): Promise<OpeningVariation[]> => {
  const cacheKey = openingVariationsCacheKey(params);
  const cached = openingVariationsCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const inFlight = inFlightOpeningVariations.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
    try {
      const { data } = await gamesApi.get<{
        variations: OpeningVariation[];
      }>('/openings/variations', {
        params: {
          opening: params.opening,
          source: params.source,
          ...(params.familyId != null ? { familyId: params.familyId } : {}),
        },
      });
      openingVariationsCache.set(cacheKey, data.variations);
      return data.variations;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, {
          action: `Failed to load variations for ${params.opening}`,
        }),
      );
    } finally {
      inFlightOpeningVariations.delete(cacheKey);
    }
  })();

  inFlightOpeningVariations.set(cacheKey, request);
  return request;
};

export const prefetchOpeningGames = (params: {
  opening: string;
  source: GameSource;
  eco?: string;
  limit?: number;
  offset?: number;
  player?: string;
}): void => {
  void fetchOpeningGames(params).catch(() => {});
};

export const fetchOpeningGames = async (params: {
  opening: string;
  source: GameSource;
  eco?: string;
  familyId?: number;
  lineId?: number;
  limit?: number;
  offset?: number;
  player?: string;
}): Promise<
  PaginatedGamesResult & {
    opening: string;
    eco?: string;
    source: GameSource;
  }
> => {
  const cacheKey = openingGamesCacheKey(params);
  const cached = openingGamesCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const inFlight = inFlightOpeningGames.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
    try {
      const { data } = await gamesApi.get<{
        opening: string;
        eco?: string;
        source: GameSource;
        games: ReplayGameSummary[];
        offset: number;
        hasMore?: boolean;
      }>('/openings/games', {
        params: {
          opening: params.opening,
          source: params.source,
          limit: params.limit ?? GAMES_BROWSE_PAGE_SIZE,
          offset: params.offset ?? 0,
          ...(params.eco ? { eco: params.eco } : {}),
          ...(params.familyId != null ? { familyId: params.familyId } : {}),
          ...(params.lineId != null ? { lineId: params.lineId } : {}),
          ...(params.player?.trim() ? { player: params.player.trim() } : {}),
        },
      });
      const result = {
        opening: data.opening,
        eco: data.eco,
        source: data.source,
        games: data.games,
        offset: data.offset ?? params.offset ?? 0,
        hasMore: data.hasMore === true,
      };
      openingGamesCache.set(cacheKey, result);
      return result;
    } catch (error) {
      throw new Error(
        getApiErrorMessage(error, {
          action: `Failed to load games for ${params.opening}`,
        }),
      );
    } finally {
      inFlightOpeningGames.delete(cacheKey);
    }
  })();

  inFlightOpeningGames.set(cacheKey, request);
  return request;
};

export const resolveOpeningBrowseTarget = async (
  opening: string,
  sources: GameSource[],
): Promise<OpeningSearchResult | null> => {
  const family = getOpeningFamilyName(opening);
  if (!family) {
    return null;
  }

  const results = await Promise.all(
    sources.map((source) =>
      searchOpenings({ q: family, source }).catch(() => [] as OpeningSearchResult[]),
    ),
  );
  const merged = mergeOpeningSearchResults(results.flat());
  return (
    merged.find((row) => row.opening === family) ??
    merged.find(
      (row) => row.opening.toLowerCase() === family.toLowerCase(),
    ) ??
    merged[0] ??
    null
  );
};

export const resolvePlayerBrowseTarget = async (
  username: string,
  source: GameSource,
): Promise<PlayerSearchResult | null> => {
  const trimmed = username.trim();
  if (!trimmed) {
    return null;
  }

  const results = await searchPlayers({
    q: trimmed,
    source,
    gmsOnly: source === 'twic',
  });
  return (
    results.find((row) => row.username === trimmed) ??
    results.find(
      (row) => row.username.toLowerCase() === trimmed.toLowerCase(),
    ) ??
    results[0] ??
    null
  );
};

export const resolveOpeningFromFamilySlug = async (
  familySlug: string,
  sources: GameSource[],
): Promise<OpeningSearchResult | null> => {
  const normalizedSlug = familySlug.trim().toLowerCase();
  if (!normalizedSlug) {
    return null;
  }

  const family = await fetchOpeningFamilyBySlug(normalizedSlug);
  if (!family) {
    return null;
  }

  const results = await Promise.all(
    sources.map((source) =>
      searchOpenings({ q: family.name, source }).catch(
        () => [] as OpeningSearchResult[],
      ),
    ),
  );

  const merged = mergeOpeningSearchResults(results.flat()).find(
    (row) => row.familyId === family.familyId || openingFamilySlug(row.opening) === normalizedSlug,
  );

  if (merged) {
    return {
      ...merged,
      opening: family.name,
      familyId: family.familyId,
    };
  }

  return {
    opening: family.name,
    source: sources[0] ?? 'lichess',
    gameCount: 0,
    familyId: family.familyId,
  };
};

export const resolveOpeningVariationFromPath = async (
  familySlug: string,
  variationSlug: string,
  eco: string | undefined,
  sources: GameSource[],
): Promise<{ opening: OpeningSearchResult; variation: OpeningVariation | null } | null> => {
  const opening = await resolveOpeningFromFamilySlug(familySlug, sources);
  if (!opening?.familyId) {
    return null;
  }

  const line = await fetchOpeningLineBySlugs(familySlug, variationSlug, eco);
  if (!line) {
    return null;
  }

  const variations = await fetchOpeningVariationsForSources({
    opening: opening.opening,
    sources,
    familyId: opening.familyId,
  });

  const match =
    variations.find((row) => row.lineId === line.lineId) ??
    variations.find((row) => {
      if (openingVariationSlug(row.opening) !== line.variationSlug) {
        return false;
      }
      if (eco?.trim() && row.eco?.trim().toLowerCase() !== eco.trim().toLowerCase()) {
        return false;
      }
      return true;
    }) ??
    {
      opening: line.opening,
      eco: line.eco || undefined,
      gameCount: 0,
      lineId: line.lineId,
      lichessOpening: line.opening,
      pgn: line.pgn,
    };

  return { opening, variation: match };
};

export const resolvePlayerFromBrowseSlug = async (
  playerSlug: string,
  source: GameSource,
): Promise<PlayerSearchResult | null> => {
  const normalizedSlug = playerSlug.trim().toLowerCase();
  if (!normalizedSlug) {
    return null;
  }

  try {
    const { data } = await gamesApi.get<PlayerSearchResult>(
      `/players/by-slug/${encodeURIComponent(normalizedSlug)}`,
      { params: { source } },
    );
    return data ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, {
        action: `Failed to resolve player slug "${normalizedSlug}"`,
      }),
    );
  }
};

export function clearGamesBrowseCaches(): void {
  openingVariationsCache.clear();
  inFlightOpeningVariations.clear();
  openingGamesCache.clear();
  inFlightOpeningGames.clear();
}

export const fetchPlayerOpenings = async (
  username: string,
  source: GameSource,
): Promise<{
  totalGames: number;
  openings: PlayerOpeningStat[];
}> => {
  try {
    const { data } = await gamesApi.get<{
      totalGames: number;
      openings: PlayerOpeningStat[];
    }>(`/players/${encodeURIComponent(username)}/openings`, {
      params: { source },
    });
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, {
        action: `Failed to load openings for ${username}`,
      }),
    );
  }
};
